import React from 'react';
import type {IconName, IconPrefix} from '@fortawesome/fontawesome-common-types';

import {Icon} from '@albert-io/atomic';

// A ghost list row: icon badge + two skeleton bars, mirroring a real row.
const SkeletonCard = ({icon}: {icon: IconName | [IconPrefix, IconName]}) => (
  <div className='flex w-[232px] items-center gap-x1-half rounded-x1-half border border-subtle background-primary p-x1'>
    <div className='flex h-x4-half w-x4-half shrink-0 items-center justify-center rounded-full background-brand-subtle-hover'>
      <Icon className='text-s' color='brand' icon={icon} iconStyle='regular' />
    </div>
    <div className='flex grow flex-col gap-x0-half'>
      <div className='h-x1 w-[100px] rounded-full background-bold' />
      <div className='h-x1 w-x5 rounded-full background-bold' />
    </div>
  </div>
);

/** Two overlapping ghost rows — the decorative illustration above empty-state copy. */
export const EmptyIllustration = ({icon}: {icon: IconName | [IconPrefix, IconName]}) => (
  <div aria-hidden='true' className='grid place-items-start'>
    <div className='col-start-1 row-start-1'>
      <SkeletonCard icon={icon} />
    </div>
    <div className='col-start-1 row-start-1 mt-x4 ml-x4 rounded-x1-half elevation-shadow-l2'>
      <SkeletonCard icon={icon} />
    </div>
  </div>
);

interface Props {
  illustration: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Centered dashboard empty state: a decorative illustration, copy (children), and an optional
 * action. Shared by the Assignments and People (co-teachers) empty states.
 */
export const EmptyState = ({illustration, action, children}: Props) => (
  <div className='flex w-full flex-col items-center gap-x3 py-x8 text-center'>
    {illustration}
    {children}
    {action}
  </div>
);
